/**
 * Model markup, as one unit any model view mounts: the camera view kept in the
 * route's URL (so Back and links restore it), planes made with the markup tool
 * on the viewed file's own doc, the open plane's drawing canvas, and every
 * plane's marks in 3D with pins that reopen them. A route supplies only the
 * file and how it keeps `cam` and `plane` in its URL.
 *
 * The 3D view offers markup only while a host publishes plane marks (null
 * otherwise), so the tool cannot show where nothing would handle it.
 */
import type { AutomergeUrl } from "@automerge/automerge-repo";
import { RepoContext } from "@automerge/react";
import type { CameraView } from "bear/engine/threeConsumer";
import {
  addPlane,
  type AnnotatedDoc,
} from "playground/annotations/annotations";
import { forkBranchFile } from "playground/branches/project-branch";
import type { TextDoc } from "playground/fs/types";
import type { HostPlayground } from "playground/hal/index";
import {
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { createTLSchema } from "tldraw";
import { invariant } from "ts-invariant";

import { formatCameraView, parseCameraView } from "@/bear-viewer/camera-view";
import {
  modelFrameOf,
  planeFromPick,
  planeView,
  type ModelPlaneFrame,
} from "@/bear-viewer/model-plane";
import { useBearSandbox } from "@/components/BearSandboxProvider";
import {
  currentCameraView,
  showPlaneMarks,
  showView,
  subscribeCameraView,
  subscribePlanePick,
} from "@/lib/bear-sandbox-bridge";
import {
  PlaneMarkup,
  usePlaneMarks,
  useSandboxConnected,
} from "@/panes/plane-markup";

/** A view in the route's URL: the camera, the plane open on it, and a
 *  redline's thread to open there (a reference followed back). */
export type MarkupView = {
  cam: string | undefined;
  plane: string | undefined;
  thread?: string;
};

/**
 * The view a redline's thread opens in: its plane, square-on (the camera a
 * pin opens it with), with the thread named. Null for a thread that isn't on
 * a mark.
 */
export function markupViewOf(
  doc: AnnotatedDoc,
  threadId: string,
): MarkupView | null {
  const thread = doc[`comment:${threadId}`];
  if (thread?.anchor.kind !== "marks") return null;
  const plane = doc[`plane:${thread.anchor.plane}`];
  invariant(plane, `No plane ${thread.anchor.plane}`);
  return {
    cam: formatCameraView(planeView(modelFrameOf(plane))),
    plane: plane.id,
    thread: threadId,
  };
}

export interface ModelMarkupHost extends MarkupView {
  /** The model file the view renders, whose doc holds its planes. */
  file: string;
  /** The project index it is read from: main, or a task's branch. */
  indexUrl: AutomergeUrl;
  /** Put a view in the route's URL, as a new entry or in place of this one. */
  navigate: (view: MarkupView, mode: "push" | "replace") => void;
}

/** Counts project tree changes, for re-deriving what reads the tree. */
export function useTreeVersion(playground: HostPlayground): number {
  const [version, setVersion] = useState(0);
  useEffect(
    () => playground.watchTree(() => setVersion((n) => n + 1)),
    [playground],
  );
  return version;
}

/**
 * The file's doc that markup writes to, followed as the tree changes (a
 * checkpoint restore can put another doc at the path). A file still pinned
 * to main on a task's branch is forked first, so branch markup never lands
 * on main.
 */
function useWritableDoc(indexUrl: AutomergeUrl, file: string) {
  const repo = useContext(RepoContext);
  const { playground } = useBearSandbox();
  const tree = useTreeVersion(playground);
  const key = `${indexUrl}:${file}`;
  const [resolved, setResolved] = useState<{
    key: string;
    url: AutomergeUrl;
  } | null>(null);
  useEffect(() => {
    invariant(repo, "Automerge repo is not available");
    let live = true;
    void (async () => {
      const found = await playground.resolve(file);
      invariant(found, `No file at ${file}`);
      if (found.inode.version) return forkBranchFile(repo, indexUrl, file);
      invariant(
        found.inode.uri.startsWith("automerge:"),
        `Not an Automerge file: ${file}`,
      );
      return found.inode.uri as AutomergeUrl;
    })().then(
      (url) => {
        if (!live) return;
        setResolved((was) =>
          was?.key === key && was.url === url ? was : { key, url },
        );
      },
      (error: unknown) => {
        toast.error(error instanceof Error ? error.message : String(error));
      },
    );
    return () => {
      live = false;
    };
  }, [file, indexUrl, key, playground, repo, tree]);
  return resolved?.key === key ? resolved.url : undefined;
}

/**
 * The camera lives in the URL (`cam`), kept by whichever view owns it: an
 * open plane (`PlaneMarkup`), or else the 3D view, here. Each time the 3D
 * camera rests it replaces the current entry, as it does when the URL has
 * none (the view's taker settled while a plane still owned it); a `cam` this
 * didn't write (Back, a pasted link) moves it.
 */
function ModelCameraInUrl({
  cam,
  navigate,
}: Pick<ModelMarkupHost, "cam" | "navigate">) {
  const connected = useSandboxConnected();
  const written = useRef<string | undefined>(undefined);
  const record = useEffectEvent((view: CameraView) => {
    written.current = formatCameraView(view);
    navigate({ cam: written.current, plane: undefined }, "replace");
  });

  useEffect(() => {
    if (!connected) return;
    return subscribeCameraView(record);
  }, [connected]);

  useEffect(() => {
    const view = currentCameraView();
    if (cam || !connected || !view) return;
    record(view);
  }, [cam, connected]);

  useEffect(() => {
    if (!cam || !connected || cam === written.current) return;
    const view = parseCameraView(cam);
    invariant(view, `Not a camera view: ${cam}`);
    showView(view);
  }, [cam, connected]);
  return null;
}

/**
 * The markup tool's pick becomes a plane on the file's doc, made against what
 * the file rendered, and opens square-on as a new entry (so Back returns to
 * the view it was made from).
 */
function usePlaneMaking(
  host: ModelMarkupHost,
  docUrl: AutomergeUrl | undefined,
) {
  const repo = useContext(RepoContext);
  const { getModelSelection } = useBearSandbox();
  const { file, navigate } = host;
  useEffect(() => {
    if (!docUrl || !repo) return;
    return subscribePlanePick((pick) => {
      void (async () => {
        const view = currentCameraView();
        invariant(view, "The 3D view hasn't reported its camera yet");
        const { exportName, props } = getModelSelection(file);
        const frame = planeFromPick(pick, view, {
          exportName: exportName || null,
          props: props ?? null,
        });
        const handle = await repo.find<TextDoc>(docUrl);
        const id = addPlane(handle, frame, { ...createTLSchema().serialize() });
        navigate(
          { cam: formatCameraView(planeView(frame)), plane: id },
          "push",
        );
      })().catch((error: unknown) => {
        toast.error(error instanceof Error ? error.message : String(error));
      });
    });
  }, [docUrl, file, getModelSelection, navigate, repo]);
}

/** Everything markup on a model view does, for the view to mount. */
export function ModelMarkup({ host }: { host: ModelMarkupHost }) {
  const docUrl = useWritableDoc(host.indexUrl, host.file);
  const { cam, plane, thread, navigate } = host;

  usePlaneMaking(host, docUrl);
  const openPlane = useCallback(
    (planeId: string, frame: ModelPlaneFrame) =>
      navigate(
        { cam: formatCameraView(planeView(frame)), plane: planeId },
        "push",
      ),
    [navigate],
  );
  usePlaneMarks(docUrl, plane ?? null, openPlane);
  // Unmounted, the host is gone: the 3D view stops offering markup.
  useEffect(() => () => showPlaneMarks(null), []);

  if (!plane) return <ModelCameraInUrl cam={cam} navigate={navigate} />;
  return docUrl ? (
    <Suspense fallback={null}>
      <PlaneMarkup
        key={`${docUrl}:${plane}`}
        docUrl={docUrl}
        file={host.file}
        thread={thread}
        planeId={plane}
        cam={cam}
        onCamera={(next) => navigate({ cam: next, plane, thread }, "replace")}
        onClose={() => navigate({ cam, plane: undefined }, "replace")}
        // Its taker owns the camera now; the view it settles on writes `cam`.
        onViewTaken={() =>
          navigate({ cam: undefined, plane: undefined }, "replace")
        }
      />
    </Suspense>
  ) : null;
}
